//////////////////
// IMPORTS
//////////////////
// libraries
import * as React from "react";
import { connect } from "react-redux";
// health-period-picker imports
import { IContainer, IProps, IState } from "./HealthPeriodPickerTypes";
// state
import { IReduxState } from "#webapp/src/state/reducers/root.reducer";
// other internals
import { ChromeSite, getChromeSite } from "#webapp/src/util/chrome-extension.util";
import { EHealthTimePeriod, getFetchInfo } from "#webapp/src/util/team-health.util";
// material-ui
import { FormControl, InputLabel, MenuItem, Select } from "@material-ui/core";

//////////////////
// COMPONENT
//////////////////
class HealthPeriodPickerCmpt extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);
    // init to CalendarView if nothing is passed in
    const defaultTimePeriod =
      getChromeSite() === ChromeSite.Gcal
        ? EHealthTimePeriod.CalendarView
        : EHealthTimePeriod.ThisWeek;
    const timePeriod = props.initialPeriod || defaultTimePeriod;
    const calViewState = props.overrideCalViewState || props.chromeCalendarView;
    const dateLabel = getFetchInfo(props.orgId, calViewState, timePeriod).dateLabel;
    this.state = {
      timePeriod,
      dateLabel,
      selectOpen: false,
    };
  }

  // ~-~-~-~-~-~-~-
  // Life-Cycle
  // ~-~-~-~-~-~-~-
  private hasCalViewChanged = (newCalView: IProps["chromeCalendarView"]) => {
    const oldCalView = this.props.chromeCalendarView;
    if (!oldCalView && !newCalView) {
      // note: this is critical to avoid continual refetch if the calViewState isn't getting
      // sent down for some reason. For example, when viewing /chrome on prod
      return false;
    }
    return (
      !oldCalView ||
      !newCalView ||
      oldCalView.activeView !== newCalView.activeView ||
      oldCalView.selectedDay !== newCalView.selectedDay ||
      oldCalView.month !== newCalView.month ||
      oldCalView.year !== newCalView.year
    );
  };

  // handles refetching when calViewState changes
  public UNSAFE_componentWillReceiveProps(nextProps: IProps) {
    const { timePeriod } = this.state;

    // only needs refetch if the current timePeriod is calView, and it's changed
    const calViewPeriodSelected = timePeriod === EHealthTimePeriod.CalendarView;
    const calViewChanged = this.hasCalViewChanged(nextProps.chromeCalendarView);
    if (!calViewPeriodSelected || !calViewChanged) {
      return;
    }

    // refech requested or chromeViewChanged
    this.update(nextProps.orgId, nextProps.chromeCalendarView, timePeriod);
  }

  // ~-~-~-~-~-~-~-
  // Updating
  // ~-~-~-~-~-~-~-
  private update = (
    orgId: string,
    calView: IProps["chromeCalendarView"],
    timePeriod: EHealthTimePeriod,
  ) => {
    const { fetchVariables, dateLabel } = getFetchInfo(orgId, calView, timePeriod);
    this.setState({ timePeriod, dateLabel });
    if (this.props.changeDateRange) {
      this.props.changeDateRange(fetchVariables.rangeMax, fetchVariables.rangeMin);
    } else {
      this.props.onChange(fetchVariables);
    }
  };

  // ~-~-~-~-~-~-~-
  // Handlers
  // ~-~-~-~-~-~-~-
  private setSelectOpenState = (selectOpen: boolean) => {
    this.setState({ selectOpen });
  };

  private onSelection = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { orgId, chromeCalendarView } = this.props;
    const timePeriod = e.target.value as any; // as EHealthTimePeriod doesnt work for some reason
    this.update(orgId, chromeCalendarView, timePeriod);
  };

  // ~-~-~-~-~-~-~-
  // Rendering
  // ~-~-~-~-~-~-~-
  public render() {
    const { label, style, showExtended } = this.props;
    const { timePeriod, dateLabel, selectOpen } = this.state;
    return (
      <FormControl style={style}>
        {label ? (
          <InputLabel shrink htmlFor="cw-health-period-picker-select">
            {label}
          </InputLabel>
        ) : (
          ""
        )}
        {/* FIXME: (skopec) use DS component */}
        <Select
          value={timePeriod}
          onChange={this.onSelection}
          name="Health Period Picker"
          disabled={this.props.disabled}
          onOpen={() => this.setSelectOpenState(true)}
          onClose={() => this.setSelectOpenState(false)}
          inputProps={{
            name: "Health Period Picker",
            id: "cw-health-period-picker-select",
          }}
        >
          {getChromeSite() === ChromeSite.Gcal ? (
            <MenuItem value={EHealthTimePeriod.CalendarView}>
              {timePeriod === EHealthTimePeriod.CalendarView && !selectOpen
                ? dateLabel
                : "Week selected in calendar"}
            </MenuItem>
          ) : null}
          {showExtended ? (
            <MenuItem value={EHealthTimePeriod.Last3Months}>Last 3 Months</MenuItem>
          ) : null}
          {showExtended ? (
            <MenuItem value={EHealthTimePeriod.LastMonth}>Last Month</MenuItem>
          ) : null}
          <MenuItem value={EHealthTimePeriod.TwoWeeksAgo}>Two weeks ago</MenuItem>
          <MenuItem value={EHealthTimePeriod.LastWeek}>Last week</MenuItem>
          <MenuItem value={EHealthTimePeriod.ThisWeek}>This week</MenuItem>
          <MenuItem value={EHealthTimePeriod.NextWeek}>Next week</MenuItem>
          <MenuItem value={EHealthTimePeriod.TwoWeeksAhead}>In two weeks</MenuItem>
        </Select>
      </FormControl>
    );
  }
}

//////////////////
// REDUX
//////////////////
function mapStateToProps(state: IReduxState, _ownProps: IContainer) {
  return {
    chromeCalendarView: state.webExtension.calendarView,
  };
}

export const HealthPeriodPicker = connect(mapStateToProps, {})(HealthPeriodPickerCmpt);
